/** Dashed-border "New folder" card — inline name input that submits on Enter / blurs. */
import { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateFolder } from '@/shared/api/hooks';
import { DEFAULT_FOLDER_COLOR } from '@/interfaces/learner/ui';

export function NewFolderCard() {
  const createFolder = useCreateFolder();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const handleSubmit = () => {
    const name = value.trim();
    if (!name) {
      setEditing(false);
      setValue('');
      return;
    }
    createFolder.mutate(
      { name, color: DEFAULT_FOLDER_COLOR },
      {
        onSuccess: () => {
          setEditing(false);
          setValue('');
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to create folder');
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSubmit();
    if (e.key === 'Escape') {
      setEditing(false);
      setValue('');
    }
  };

  return (
    <div
      onClick={() => !editing && setEditing(true)}
      style={{
        minHeight: 110,
        borderRadius: 'var(--control-radius-md)',
        border: '1.5px dashed var(--color-surface-border)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        cursor: editing ? 'default' : 'pointer',
        padding: '16px 12px',
        boxSizing: 'border-box',
        transition: 'border-color 0.15s, background 0.15s',
      }}
      className="hover:bg-[var(--color-surface-muted)]"
    >
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSubmit}
          placeholder="Folder name"
          style={{
            width: '100%',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            borderBottom: '1px solid var(--color-accent)',
            color: 'var(--color-text)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--type-list-title-size)',
            fontWeight: 'var(--type-list-title-weight)',
            textAlign: 'center',
            padding: '2px 4px',
          }}
        />
      ) : (
        <>
          <Plus size={22} strokeWidth={1.75} style={{ color: 'var(--color-text-muted)' }} />
          <span
            className="text-list-meta-muted"
            style={{
              color: 'var(--color-text-muted)',
            }}
          >
            New folder
          </span>
        </>
      )}
    </div>
  );
}
